// SEQ-180 phase 1: /pnl-analytics. Cross-project profit, overtime and
// patching analytics computed from the same P&L engine every project's
// Status tab already uses (plan section 6, decision D2).
//
// SEQ-180 PHASE 2. The page no longer composes components by hand. It picks a
// dashboard, turns that dashboard's widget configs into the smallest set of
// RPC calls that serves them (dashboardRequestKeys.ts, plan decision D20),
// and renders whatever widget list it was given through one renderer
// (PnlWidgetRenderer.tsx).
//
// WHAT A USER WHO NEVER OPENS THE BUILDER SEES. When they own no dashboard
// and the organisation has shared none, the page renders PHASE1_WIDGETS read
// only as "Company overview" (plan decision D11). Those 13 configs resolve to
// the same two RPC calls the phase 1 page made, at the same two grains, and
// the KPI cards still read a client-side sum of the project-grain rows, so
// the numbers are the same numbers rather than a server-side re-aggregation
// of them.
//
// The sticky filter bar, the admin unmapped panel and the footer note are
// unchanged and still sit where they did.
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { BarChart3, AlertTriangle, Info, LayoutDashboard, Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useProjects } from "../hooks/useProjects";
import { useSafeMode } from "../contexts/SafeModeContext";
import { Modal, useToast } from "../components/ui/ToastModal";
import { getOrganizationId } from "../lib/supabase";
import type { PnlAnalyticsQueryFilters } from "../features/pnlAnalytics/usePnlAnalytics";
import {
  DEFAULT_PNL_FILTER_STATE,
  resolvePnlDateRange,
  type PnlAnalyticsFilterState,
} from "../features/pnlAnalytics/filterState";
import {
  buildPnlRequestPlan,
  resolveWidgetFilters,
  resolveWidgetRange,
  type PnlDashboardPageFilters,
} from "../features/pnlAnalytics/dashboardRequestKeys";
import { usePnlDashboardData } from "../features/pnlAnalytics/usePnlDashboardData";
import { usePnlDashboards, type PnlDashboardWidgetSeed } from "../features/pnlAnalytics/usePnlDashboards";
import { usePnlDashboardWidgets, DEFAULT_WIDGET_LAYOUT } from "../features/pnlAnalytics/usePnlDashboardWidgets";
import { usePnlMetricRegistry } from "../features/pnlAnalytics/usePnlMetricRegistry";
import { buildKeyViews, EMPTY_KEY_VIEW } from "../features/pnlAnalytics/widgetDataset";
import { newWidgetDraft } from "../features/pnlAnalytics/widgetEditorState";
import { profitParetoDraft } from "../features/pnlAnalytics/gridEditorState";
import type { DrillDownSource } from "../features/pnlAnalytics/drillDown";
import { memberProjectScopeNote } from "../features/pnlAnalytics/memberScope";
import {
  PHASE1_WIDGETS,
  type PnlWidgetConfig,
  type PnlWidgetLayout,
} from "../lib/pnl/pnlWidgetSchema";
import { resolveMetricUnit } from "../lib/pnl/pnlAnalytics";
import { PnlDashboardSkeleton } from "../components/pnlAnalytics/PnlDashboardSkeleton";
import "../components/pnlAnalytics/PnlAnalyticsPage.css";
import { PnlAnalyticsFilterBar } from "../components/pnlAnalytics/PnlAnalyticsFilterBar";
import { PnlDashboardBar } from "../components/pnlAnalytics/PnlDashboardBar";
import { PnlDashboardGrid, type PnlGridWidget } from "../components/pnlAnalytics/PnlDashboardGrid";
import { PnlWidgetRenderer } from "../components/pnlAnalytics/PnlWidgetRenderer";
import { PnlWidgetEditorDrawer } from "../components/pnlAnalytics/PnlWidgetEditorDrawer";
import { PnlDrillDownPanel } from "../components/pnlAnalytics/PnlDrillDownPanel";
import { PnlAdminUnmapped } from "../components/pnlAnalytics/PnlAdminUnmapped";
import { PnlArchivedValues } from "../components/pnlAnalytics/PnlArchivedValues";
import { FixtureDataBadge } from "../components/pnlAnalytics/FixtureDataBadge";

// Plan decision D8: production status vocabulary, not the ticket's.
const STATUS_VALUES = new Set(["active", "upcoming", "completed"]);

interface EditorTarget {
  /** The row id of the widget being edited, or null for a new one. */
  id: string | null;
  config: PnlWidgetConfig;
  layout: PnlWidgetLayout;
}

export function PnlAnalyticsPage() {
  const { isAdmin, isSuperAdmin, user } = useAuth();
  const { isSafeMode } = useSafeMode();
  const { projects, loading: projectsLoading, error: projectsError } = useProjects();
  const { addToast } = useToast();
  const organizationId = getOrganizationId();

  const [filterState, setFilterState] = useState<PnlAnalyticsFilterState>(DEFAULT_PNL_FILTER_STATE);
  // `undefined` means "no explicit choice yet", which resolves to the user's
  // own default dashboard below. `null` means they explicitly chose the
  // read-only company overview. Deriving this beats an effect that would have
  // to setState on every dashboards load.
  const [selection, setSelection] = useState<string | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [savingWidget, setSavingWidget] = useState(false);
  // SEQ-199: the grid makes its own project_month call, so the page Refresh bumps a tick it listens to.
  const [refreshTick, setRefreshTick] = useState(0);
  const [addingPareto, setAddingPareto] = useState(false);
  const [drill, setDrill] = useState<{ source: DrillDownSource; config: PnlWidgetConfig } | null>(null);
  const [pendingWidgetDelete, setPendingWidgetDelete] = useState<string | null>(null);

  const dashboards = usePnlDashboards(organizationId, user?.id ?? null);
  const registry = usePnlMetricRegistry();

  const autoSelectedId = useMemo(() => {
    const own = dashboards.dashboards.filter((d) => d.ownerId === user?.id);
    return own.find((d) => d.isDefault)?.id ?? own[0]?.id ?? dashboards.dashboards.find((d) => d.isShared)?.id ?? null;
  }, [dashboards.dashboards, user?.id]);

  const activeDashboardId = selection === undefined ? autoSelectedId : selection;
  const activeDashboard = dashboards.dashboards.find((d) => d.id === activeDashboardId) ?? null;
  const isDefaultDashboard = activeDashboardId === null;

  const widgets = usePnlDashboardWidgets(activeDashboardId);

  const canEdit = activeDashboard !== null && (activeDashboard.ownerId === user?.id || isAdmin || isSuperAdmin);
  const isEditing = editing && canEdit;

  // Memoised: a fresh [] every render would re-key the request plan and
  // re-issue every call on every keystroke elsewhere on the page.
  const savedPresets = useMemo(() => activeDashboard?.datePresets ?? [], [activeDashboard]);

  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const dateRange = useMemo(
    () => resolvePnlDateRange(filterState, todayIso, savedPresets),
    [filterState, todayIso, savedPresets],
  );

  const queryFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => ({
      from: dateRange.from,
      to: dateRange.to,
      projectIds: filterState.projectIds,
      statuses: filterState.statuses,
    }),
    [dateRange, filterState.projectIds, filterState.statuses],
  );

  const pageFilters = useMemo<PnlDashboardPageFilters>(
    () => ({ range: dateRange, projectIds: filterState.projectIds, statuses: filterState.statuses }),
    [dateRange, filterState.projectIds, filterState.statuses],
  );

  /** The widget list on screen: the code-defined default, or the saved rows. */
  const gridWidgets = useMemo<PnlGridWidget[]>(() => {
    if (isDefaultDashboard) {
      return PHASE1_WIDGETS.map((config) => ({
        id: config.widgetId,
        config,
        layout: config.layout ?? DEFAULT_WIDGET_LAYOUT,
      }));
    }
    return widgets.widgets.map((row) => ({ id: row.id, config: row.config, layout: row.layout }));
  }, [isDefaultDashboard, widgets.widgets]);

  const plan = useMemo(
    () => buildPnlRequestPlan(gridWidgets.map((w) => w.config), pageFilters, savedPresets, todayIso, registry.metrics),
    [gridWidgets, pageFilters, savedPresets, todayIso, registry.metrics],
  );

  const dataset = usePnlDashboardData(plan);
  const refreshDataset = dataset.refresh;
  const refreshAll = useCallback(() => {
    setRefreshTick((t) => t + 1);
    refreshDataset();
  }, [refreshDataset]);
  const views = useMemo(() => buildKeyViews(plan.keys, dataset.byKey, projects), [plan.keys, dataset.byKey, projects]);

  // SEQ-240 audit B, row 30. "All projects" means every project YOU can see. A
  // member sees only the projects they belong to or created, so their totals
  // can read 0 or one project's figures with nothing saying why.
  const memberScopeNote = useMemo(
    () => (isAdmin || isSuperAdmin || projectsLoading || projectsError ? null : memberProjectScopeNote(projects.length)),
    [isAdmin, isSuperAdmin, projectsLoading, projectsError, projects.length],
  );

  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => STATUS_VALUES.has(p.status ?? "active"))
        .map((p) => ({ id: p.id, name: p.job_location, status: p.status ?? "active" })),
    [projects],
  );

  // Every project, not only the filtered or active ones: a batch cleared from
  // a project that has since been completed still has to read as that project
  // rather than as a uuid.
  const projectNamesById = useMemo(
    () => new Map(projects.map((p) => [p.id, p.job_location])),
    [projects],
  );

  const unitByMetric = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const metric of registry.metrics) lookup[metric.metricKey] = resolveMetricUnit(metric);
    return lookup;
  }, [registry.metrics]);

  const hasData = plan.keys.length === 0 || plan.keys.some((key) => views[key.key]?.loaded);

  // ── Dashboard actions ────────────────────────────────────────────────────

  const seedFromCurrent = useCallback(
    (): PnlDashboardWidgetSeed[] =>
      gridWidgets.map((widget, index) => ({ config: widget.config, layout: widget.layout, position: index })),
    [gridWidgets],
  );

  const handleCreate = useCallback(
    async (name: string) => {
      const created = await dashboards.createDashboard(name, []);
      if (created) {
        setSelection(created.id);
        setEditing(true);
      }
    },
    [dashboards],
  );

  const handleSaveCopy = useCallback(
    async (name: string) => {
      const created = await dashboards.createDashboard(name, seedFromCurrent());
      if (created) {
        setSelection(created.id);
        addToast({ type: "success", title: `"${created.name}" is yours to edit`, message: "Every widget was copied across." });
      }
    },
    [dashboards, seedFromCurrent, addToast],
  );

  const handleDuplicate = useCallback(
    async (name: string) => {
      // The read-only default has no rows to read, so a duplicate of it is
      // the same operation as "Save a copy": seed from what is on screen.
      const created = isDefaultDashboard
        ? await dashboards.createDashboard(name, seedFromCurrent())
        : await dashboards.duplicateDashboard(activeDashboardId as string, name);
      if (created) setSelection(created.id);
    },
    [dashboards, isDefaultDashboard, seedFromCurrent, activeDashboardId],
  );

  const handleDelete = useCallback(async () => {
    if (!activeDashboardId) return;
    const deleted = await dashboards.deleteDashboard(activeDashboardId);
    if (deleted) {
      setSelection(undefined);
      setEditing(false);
    }
  }, [dashboards, activeDashboardId]);

  const handleSavePreset = useCallback(
    (name: string, from: string, to: string) => {
      if (!activeDashboard) return;
      const preset = { id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, name, from, to };
      void dashboards.setDatePresets(activeDashboard.id, [...activeDashboard.datePresets, preset]);
      setFilterState((state) => ({ ...state, customPresetId: preset.id }));
    },
    [activeDashboard, dashboards],
  );

  const handleDeletePreset = useCallback(
    (id: string) => {
      if (!activeDashboard) return;
      void dashboards.setDatePresets(
        activeDashboard.id,
        activeDashboard.datePresets.filter((preset) => preset.id !== id),
      );
    },
    [activeDashboard, dashboards],
  );

  // ── Widget actions ───────────────────────────────────────────────────────

  const handleSaveWidget = useCallback(
    async (config: PnlWidgetConfig, layout: PnlWidgetLayout) => {
      if (!editorTarget) return;
      setSavingWidget(true);
      const saved =
        editorTarget.id === null
          ? await widgets.addWidget(config, layout)
          : await widgets.updateWidget(editorTarget.id, config);
      if (saved && editorTarget.id !== null) await widgets.resizeWidget(editorTarget.id, layout);
      setSavingWidget(false);
      if (saved) setEditorTarget(null);
    },
    [editorTarget, widgets],
  );

  // Design audit, MEDIUM: this used window.confirm, which is an unstyled
  // browser dialog with no relation to the app, no focus management of its
  // own that the app controls, and a different look on every platform. The
  // dashboard delete already goes through the shared Modal, so a widget
  // delete goes through the same one and the two destructive actions read
  // alike. A widget is cheap to rebuild, so it asks once rather than making
  // the user type a name back the way a whole dashboard does.
  const handleDeleteWidget = useCallback((id: string) => {
    setPendingWidgetDelete(id);
  }, []);

  const pendingDeleteWidget = gridWidgets.find((w) => w.id === pendingWidgetDelete) ?? null;

  const confirmDeleteWidget = useCallback(() => {
    if (pendingWidgetDelete) void widgets.deleteWidget(pendingWidgetDelete);
    setPendingWidgetDelete(null);
  }, [pendingWidgetDelete, widgets]);

  const editorKey = editorTarget ? `${editorTarget.id ?? "new"}-${editorTarget.config.widgetId}` : "closed";
  const editorPreviewView = editorTarget
    ? (views[plan.keyByWidgetId[editorTarget.config.widgetId] ?? ""] ?? views[plan.keys[0]?.key ?? ""] ?? EMPTY_KEY_VIEW)
    : EMPTY_KEY_VIEW;

  const drillUnit = drill ? ((unitByMetric[drill.config.metric] ?? "currency") as never) : ("currency" as never);
  const drillMetricName = drill
    ? (registry.metrics.find((m) => m.metricKey === drill.config.metric)?.displayName ?? drill.config.metric)
    : "";

  return (
    // Named so the page's controls can be given a 44px target where the pointer
    // is coarse, in one rule, rather than every button carrying its own media
    // query. A rendered audit measured 40 controls under 44x44 at 390px.
    <div className="pnl-analytics-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 10 }}>
            <BarChart3 size={24} aria-hidden="true" style={{ color: "var(--color-primary)" }} />
            P&amp;L analytics
            {dataset.isFixtureData && <FixtureDataBadge />}
          </h1>
          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: 4 }}>
            Which projects are making money, which are burning overtime, which are burning patching.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {isSafeMode && (
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "var(--seq-blue)" }}>
              <ShieldCheck size={14} /> Safe Mode: dollar values hidden
            </span>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={refreshAll} disabled={dataset.loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <PnlDashboardBar
        dashboards={dashboards.dashboards}
        selectedId={activeDashboardId}
        onSelect={(id) => {
          setSelection(id);
          setEditing(false);
          setFilterState((state) => ({ ...state, customPresetId: null }));
        }}
        canEdit={canEdit}
        editing={isEditing}
        onToggleEditing={() => setEditing((value) => !value)}
        onCreate={(name) => void handleCreate(name)}
        onDuplicate={(name) => void handleDuplicate(name)}
        onRename={(name) => activeDashboardId && void dashboards.renameDashboard(activeDashboardId, name)}
        onToggleShared={() =>
          activeDashboard && void dashboards.setDashboardShared(activeDashboard.id, !activeDashboard.isShared)
        }
        onDelete={() => void handleDelete()}
        onSaveCopy={(name) => void handleSaveCopy(name)}
      />

      <PnlAnalyticsFilterBar
        projects={projectOptions}
        value={filterState}
        onChange={setFilterState}
        savedPresets={savedPresets}
        canManagePresets={canEdit}
        onSavePreset={handleSavePreset}
        onDeletePreset={handleDeletePreset}
      />

      {plan.capExceeded && (
        <div className="card" style={{ padding: "0.9rem 1.2rem", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={18} style={{ color: "#f59e0b", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.6, margin: 0 }}>
            This dashboard needs more than eight separate queries, so {plan.droppedWidgetIds.length} widget
            {plan.droppedWidgetIds.length === 1 ? "" : "s"} did not load. Give more widgets the same date range, or
            move some onto a second dashboard.
          </p>
        </div>
      )}

      {registry.registryError && (
        <div className="card" role="alert" style={{ padding: "1rem 1.5rem", marginBottom: 20 }}>
          <AlertTriangle size={20} style={{ color: "#f59e0b", marginBottom: 6 }} />
          <p style={{ color: "var(--text-secondary)", marginBottom: 0 }}>{registry.registryError}</p>
        </div>
      )}
      {/* React review, HIGH: `dataset.error` is now set only when EVERY request
          key failed, so this full-page card no longer hides a dashboard whose
          other keys loaded. A single failed key renders on the widgets that
          share it, through PnlWidgetRenderer's `loadError`. */}
      {dataset.error && (
        /* Design audit: this was centred, hardcoded #f59e0b, printed the raw
           error and carried no role, six lines above an empty state rebuilt to
           the opposite rule. Same treatment now, and `role="alert"` because a
           dashboard that failed to load is the one thing on this page a
           screen reader must be told without being asked. */
        <div className="card pnl-page-alert" role="alert">
          <AlertTriangle size={28} aria-hidden="true" className="pnl-page-alert-icon" />
          <h2 className="pnl-page-alert-title">This dashboard could not load its data</h2>
          <p className="pnl-page-alert-body">
            Nothing here is out of date, because nothing arrived. The filters and the widgets are unchanged, so a
            retry asks for exactly the same thing again.
          </p>
          <p className="pnl-page-alert-detail">{dataset.error}</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={refreshAll}>
            <RefreshCw size={14} aria-hidden="true" /> Retry
          </button>
        </div>
      )}

      {/* The skeleton draws THIS dashboard's spans, not a generic guess: the
          widget list and their layouts are resolved above, before the request
          plan that is still in flight was built from them. */}
      {!dataset.error && dataset.loading && !hasData && (
        <PnlDashboardSkeleton
          widgets={gridWidgets.map((w) => ({ id: w.id, chartType: w.config.chartType, layout: w.layout }))}
        />
      )}

      {!dataset.error && hasData && (
        <>
          {memberScopeNote !== null && (
            <aside className="pnl-page-note" role="note">
              <Info size={15} aria-hidden="true" className="pnl-page-note-icon" />
              <div>
                <p className="pnl-page-note-line">{memberScopeNote}</p>
              </div>
            </aside>
          )}
          {gridWidgets.length === 0 && !isEditing ? (
            <div className="card pnl-empty-dashboard">
              <LayoutDashboard size={28} aria-hidden="true" className="pnl-empty-dashboard-icon" />
              <h2 className="pnl-empty-dashboard-title">This dashboard has no widgets yet</h2>
              <p className="pnl-empty-dashboard-body">
                A widget is one metric, grouped the way you want to read it: profit by project, overtime by month,
                a single number for the company. Add one and it saves to this dashboard for everyone you share it
                with.
              </p>
              {canEdit ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
                  <Plus size={14} aria-hidden="true" /> Add your first widget
                </button>
              ) : (
                <p className="pnl-empty-dashboard-body">
                  You can open any dashboard shared with you from the bar above.
                </p>
              )}
            </div>
          ) : (
            <PnlDashboardGrid
              widgets={gridWidgets}
              editing={isEditing}
              onReorder={(orderedIds) => void widgets.reorderWidgets(orderedIds)}
              onResize={(id, layout) => void widgets.resizeWidget(id, layout)}
              onEditWidget={(id) => {
                const target = gridWidgets.find((w) => w.id === id);
                if (target) setEditorTarget({ id: target.id, config: target.config, layout: target.layout });
              }}
              onDeleteWidget={handleDeleteWidget}
              onAddWidget={() => {
                const draft = newWidgetDraft(gridWidgets.map((w) => w.config.widgetId));
                setEditorTarget({ id: null, config: draft.config, layout: draft.layout });
              }}
              addingProfitPareto={addingPareto}
              onAddProfitPareto={() => {
                // SEQ-199: one step, saved straight to this dashboard. The widget's settings stay editable afterwards.
                // The tile is disabled until the save settles, so a double click cannot add two.
                if (addingPareto) return;
                setAddingPareto(true);
                const draft = profitParetoDraft(gridWidgets.map((w) => w.config.widgetId));
                void widgets.addWidget(draft.config, draft.layout).finally(() => setAddingPareto(false));
              }}
              renderWidget={(widget) => (
                <PnlWidgetRenderer
                  config={widget.config}
                  view={views[plan.keyByWidgetId[widget.config.widgetId] ?? ""] ?? EMPTY_KEY_VIEW}
                  comparisonView={
                    views[plan.comparisonKeyByWidgetId[widget.config.widgetId] ?? ""] ?? null
                  }
                  metrics={registry.metrics}
                  queryFilters={queryFilters}
                  projects={projects}
                  loadError={dataset.errorByKey[plan.keyByWidgetId[widget.config.widgetId] ?? ""] ?? null}
                  onRetry={refreshAll}
                  payrollAsAt={dataset.meta?.payrollAsAt ?? null}
                  refreshTick={refreshTick}
                  onDrillDown={
                    isEditing
                      ? undefined
                      : (bucket, config) =>
                          setDrill({
                            config,
                            source: {
                              groupBy: config.groupBy,
                              bucketLabel: bucket.label,
                              projectId: bucket.projectId,
                              periodMonth: bucket.periodMonth,
                              bucketKey: bucket.key,
                              // The widget's own range and filters, the ones its
                              // bucket was worked out from (SEQ-240 audit B, row 23).
                              range: resolveWidgetRange(config, pageFilters, savedPresets, todayIso),
                              ...resolveWidgetFilters(config, pageFilters),
                            },
                          })
                  }
                />
              )}
            />
          )}

          {(isAdmin || isSuperAdmin) && dataset.meta && (
            <div style={{ marginBottom: 20 }}>
              <PnlAdminUnmapped
                unmappedCategoryStrings={dataset.meta.unmappedCategories}
                projects={projectOptions}
                onPayrollRefreshed={dataset.refresh}
              />
            </div>
          )}

          {/* Recovery route 3, beside the orphan list, because both answer the
              same question: which P&L numbers are not where somebody expects
              them to be. This panel gates itself on organisation admin AND
              financial access, the pair the archive's SELECT policy checks, and
              renders nothing at all otherwise. */}
          <div style={{ marginBottom: 20 }}>
            <PnlArchivedValues projectNames={projectNamesById} onRestored={dataset.refresh} />
          </div>

          

          {/* The wages note is the most load-bearing sentence on the page: it is
              why a month column and a total disagree, and it is pinned by
              wagesMultiplierGrain.test.ts. It was 0.75rem muted grey, centred,
              which is the least readable arrangement of a paragraph there is.
              Same words, now a note block with a rule down its edge, left
              aligned and line-length capped. Nothing about it got quieter. */}
          <aside className="pnl-page-note">
            <Info size={15} aria-hidden="true" className="pnl-page-note-icon" />
            <div>
              <p className="pnl-page-note-line">
                Payroll data as at{" "}
                {dataset.meta?.payrollAsAt ? new Date(dataset.meta.payrollAsAt).toLocaleString("en-AU") : "not yet refreshed"}.
              </p>
              <p className="pnl-page-note-line">
                Monthly figures show wages as typed, so a month here matches that month's column on the project P&amp;L
                exactly. Period, project and company totals apply the wages multiplier, matching the P&amp;L tab's Total
                column. The months therefore do not add up to the total: the total is higher by the multiplier's share of
                the wages in those months, which is the same difference the P&amp;L tab itself shows.
              </p>
            </div>
          </aside>
        </>
      )}

      {editorTarget && (
        <PnlWidgetEditorDrawer
          key={editorKey}
          open
          initialConfig={editorTarget.config}
          initialLayout={editorTarget.layout}
          metrics={registry.metrics}
          datePresets={savedPresets}
          previewRows={editorPreviewView.rows}
          previewView={editorPreviewView}
          queryFilters={queryFilters}
          projects={projects}
          saving={savingWidget}
          onClose={() => setEditorTarget(null)}
          onSave={(config, layout) => void handleSaveWidget(config, layout)}
        />
      )}

      <Modal
        isOpen={pendingDeleteWidget !== null}
        onClose={() => setPendingWidgetDelete(null)}
        onConfirm={confirmDeleteWidget}
        title="Delete widget"
        confirmText="Delete widget"
        size="sm"
      >
        <p style={{ color: "var(--text-secondary)", lineHeight: 1.6, margin: 0 }}>
          Remove <strong>{pendingDeleteWidget?.config.name}</strong> from this dashboard? The dashboard's other
          widgets are untouched, and you can add it back from the widget editor.
        </p>
      </Modal>

      <PnlDrillDownPanel
        open={drill !== null}
        source={drill?.source ?? null}
        config={drill?.config ?? null}
        projects={projects}
        unit={drillUnit}
        metricName={drillMetricName}
        onClose={() => setDrill(null)}
      />
    </div>
  );
}
